import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth.store'
import { projectsAPI } from '@/lib/api-client'
import { useWorkflowStatus, type WorkflowStatus } from '@/hooks/useWorkflowStatus'
import { usePermission } from '@/hooks/usePermission'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ClipboardList,
  FileSignature,
  HardHat,
  ShieldCheck,
  Hammer,
  Ruler,
  CheckCircle2,
  Check,
  Lightbulb,
  ArrowRight,
  ChevronLeft,
  Building2,
  MapPin,
  FolderOpen,
  RefreshCw,
  ChevronRight,
  Plus,
  Search,
  Calendar,
  MoreVertical,
  Trash2,
  Copy,
  Loader2,
} from 'lucide-react'
import { ProjectFormDialog } from './projects/ProjectFormDialog'
import { PhaseFlow } from '@/components/PhaseFlow'
import type { LucideIcon } from 'lucide-react'

// ---------------------------------------------------------------------------
// Phase definitions
// ---------------------------------------------------------------------------

interface PhaseAction {
  label: string
  path: string
  /** When a project is selected, use this path instead (`:id` will be replaced) */
  projectPath?: string
  /** If true, this action requires a project to be selected */
  requiresProject: boolean
  /** Evaluates whether this action is already done */
  doneCheck?: (ctx: { projectId: string | null; project: any }) => boolean
  /** If true, this action is optional and shown with a visual indicator */
  optional?: boolean
}

export interface Phase {
  number: number
  name: string
  icon: LucideIcon
  description: string
  longDescription: string
  checklist: string[]
  tip: string
  actions: PhaseAction[]
}

export const PHASES: Phase[] = [
  {
    number: 1,
    name: 'Planejamento',
    icon: ClipboardList,
    description:
      'Criação do projeto, definição de escopo, plantas baixas e orçamento preliminar da obra.',
    longDescription:
      'Nesta fase inicial, o projeto ganha forma: são definidos escopo, cronograma macro, plantas baixas e o orçamento preliminar. Um bom planejamento reduz retrabalhos e garante previsibilidade ao longo de toda a obra.',
    checklist: [
      'Definir escopo e objetivos do projeto',
      'Definir plantas, blocos e unidades',
      'Estimar orçamento preliminar',
      'Criar cronograma macro de fases',
      'Registrar o projeto no sistema',
    ],
    tip: 'Comece criando o projeto — isso desbloqueia todas as outras funcionalidades da plataforma.',
    actions: [
      { label: 'Criar Projeto', path: '/projects', requiresProject: false, doneCheck: ({ projectId }) => !!projectId },
      { label: 'Definir Plantas, Blocos e Unidades', path: '/units', projectPath: '/units?project=:id', requiresProject: true, doneCheck: ({ project }) => (project?._count?.units ?? 0) > 0, optional: true },
      { label: 'Associar Atividades ao Projeto', path: '/projects', projectPath: '/projects/:id/activities', requiresProject: true, doneCheck: ({ project }) => (project?._count?.activities ?? 0) > 0 },
      { label: 'Calculadora de Materiais', path: '/levantamento', projectPath: '/projects/:id/levantamento', requiresProject: true, doneCheck: ({ projectId }) => !!projectId },
    ],
  },
  {
    number: 2,
    name: 'Contratação',
    icon: FileSignature,
    description:
      'Montagem da equipe do projeto — colaboradores, empreiteiros, fornecedores e corretores.',
    longDescription:
      'Nesta fase você define quem vai compor a equipe: colaboradores internos (supervisor, mestre), empreiteiros que executam as atividades, fornecedores de materiais e serviços, e corretores de vendas. Uma equipe bem definida garante responsabilidade clara na execução.',
    checklist: [
      'Definir equipe interna (supervisor, mestre de obras)',
      'Cadastrar empreiteiros e vincular ao projeto',
      'Cadastrar fornecedores de materiais e consultores',
      'Vincular corretores de vendas ao projeto (opcional)',
    ],
    tip: 'Cadastre a equipe no projeto — isso permite rastrear responsabilidades por atividade, medição e venda.',
    actions: [
      { label: 'Cadastrar Colaboradores', path: '/users', projectPath: '/users?projectId=:id', requiresProject: true, doneCheck: ({ project }) => (project?._count?.projectCollaborators ?? 0) > 0 },
      { label: 'Cadastrar Empreiteiros', path: '/contractors', projectPath: '/contractors?projectId=:id', requiresProject: true, doneCheck: ({ project }) => (project?._count?.contractorProjects ?? 0) > 0 },
      { label: 'Cadastrar Fornecedores', path: '/suppliers', projectPath: '/suppliers?projectId=:id', requiresProject: true, doneCheck: ({ project }) => (project?._count?.projectSuppliers ?? 0) > 0 },
      { label: 'Cadastrar Corretores', path: '/brokers', projectPath: '/brokers?projectId=:id', requiresProject: true, optional: true, doneCheck: ({ project }) => (project?._count?.projectBrokers ?? 0) > 0 },
    ],
  },
  {
    number: 3,
    name: 'Mobilização',
    icon: HardHat,
    description:
      'Preparação do canteiro de obra e início dos trabalhos de execução.',
    longDescription:
      'Hora de iniciar a obra: o canteiro é preparado, equipe mobilizada e trabalhos começam. Esta fase marca a transição do planejamento para a execução prática.',
    checklist: [
      'Preparar canteiro e infraestrutura',
      'Mobilizar equipe e materiais',
      'Dar início aos trabalhos de execução',
      'Registrar marco inicial da obra',
      'Acompanhar progresso inicial',
    ],
    tip: 'Com a equipe definida e pronta, você está preparado para dar início à execução com responsabilidades claras.',
    actions: [
      { label: 'Vincular empreiteiros às atividades', path: '/contractors', projectPath: '/projects/:id/activities', requiresProject: true },
    ],
  },
  {
    number: 4,
    name: 'Documentação',
    icon: ShieldCheck,
    description:
      'Conferência de documentos, seguros, certidões, alvarás e habilitações dos envolvidos.',
    longDescription:
      'Antes de iniciar a obra é essencial garantir que toda a documentação esteja em ordem: alvarás, seguros, certidões e habilitações. Isso protege a empresa legalmente e evita embargos.',
    checklist: [
      'Verificar alvará de construção',
      'Conferir seguros obrigatórios',
      'Validar certidões dos fornecedores',
      'Checar habilitações dos empreiteiros',
      'Organizar pasta documental do projeto',
    ],
    tip: 'Confira os cadastros de fornecedores e empreiteiros para garantir que a documentação está em dia.',
    actions: [
      { label: 'Conferir docs de Fornecedores', path: '/suppliers', requiresProject: false },
      { label: 'Conferir docs de Empreiteiros', path: '/contractors', requiresProject: false },
    ],
  },
  {
    number: 5,
    name: 'Execução',
    icon: Hammer,
    description:
      'Acompanhamento da obra em campo — vistorias, relatórios de progresso e controle de qualidade.',
    longDescription:
      'A obra está em andamento: esta fase exige acompanhamento constante com vistorias, relatórios de progresso e controle rigoroso de qualidade. É onde o planejamento se transforma em resultado.',
    checklist: [
      'Realizar vistorias periódicas',
      'Registrar diário de obra',
      'Acompanhar cronograma de atividades',
      'Controlar qualidade dos serviços',
      'Atualizar progresso no sistema',
    ],
    tip: 'Acesse o detalhe do projeto para acompanhar atividades e progresso em tempo real.',
    actions: [
      { label: 'Acompanhar atividades do projeto', path: '/projects', projectPath: '/projects/:id/activities', requiresProject: true },
      { label: 'Gerenciar Empreiteiros em campo', path: '/contractors', requiresProject: false },
    ],
  },
  {
    number: 6,
    name: 'Medição',
    icon: Ruler,
    description:
      'Aferição do trabalho executado, aprovação das medições e liberação de pagamentos parciais.',
    longDescription:
      'Com os serviços executados, é hora de medir: aferir o trabalho realizado, aprovar as medições e liberar pagamentos parciais. Medições precisas mantêm o fluxo financeiro saudável.',
    checklist: [
      'Aferir serviços executados no período',
      'Comparar com contrato e cronograma',
      'Aprovar ou contestar medição',
      'Liberar pagamento parcial',
      'Registrar no módulo financeiro',
    ],
    tip: 'Use o módulo Financeiro para controlar pagamentos e manter o fluxo de caixa atualizado.',
    actions: [
      { label: 'Registrar transações financeiras', path: '/financial', requiresProject: false },
      { label: 'Conferir situação dos Empreiteiros', path: '/contractors', requiresProject: false },
    ],
  },
  {
    number: 7,
    name: 'Encerramento',
    icon: CheckCircle2,
    description:
      'Termo de quitação contratual, aceite definitivo e entrega formal da obra.',
    longDescription:
      'Fase final: o trabalho é aceito formalmente, termos de quitação são assinados e a obra é entregue. Um encerramento organizado protege contra pendências futuras e fecha o ciclo do projeto.',
    checklist: [
      'Realizar vistoria final completa',
      'Emitir termo de aceite definitivo',
      'Assinar quitação contratual',
      'Arquivar documentação completa',
      'Encerrar projeto no sistema',
    ],
    tip: 'Finalize o projeto e utilize Unidades para gerenciar a entrega das unidades aos compradores.',
    actions: [
      { label: 'Encerrar projeto', path: '/projects', projectPath: '/projects/:id/close', requiresProject: true },
      { label: 'Quitar pendências financeiras', path: '/financial', requiresProject: false },
      { label: 'Gerenciar Unidades', path: '/units', projectPath: '/units?project=:id', requiresProject: true },
    ],
  },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const GOLD = '#b8a378'
const GOLD_DARK = '#9a8a6a'

function getCurrentPhase(project?: any): number {
  if (!project) return 0
  const hasUnits = (project._count?.units ?? 0) > 0
  const hasActivities = (project._count?.activities ?? 0) > 0
  switch (project.status) {
    case 'PLANNING':
      return hasUnits && hasActivities ? 2 : 1
    case 'IN_PROGRESS':
      return 6
    case 'PAUSED':
      return 6
    case 'COMPLETED':
      return 8
    case 'CANCELLED':
      return 0
    default:
      return 0
  }
}

type NodeState = 'completed' | 'current' | 'future'

function getNodeState(phaseNumber: number, currentPhase: number): NodeState {
  if (currentPhase === 0) return 'future'
  if (phaseNumber < currentPhase) return 'completed'
  if (phaseNumber === currentPhase) return 'current'
  return 'future'
}

function stateLabel(state: NodeState, isPaused: boolean): string {
  if (state === 'completed') return 'Concluído'
  if (state === 'current') return isPaused ? 'Pausado' : 'Em andamento'
  return 'Pendente'
}

function stateBadgeClass(state: NodeState, isPaused: boolean): string {
  if (state === 'completed') return 'bg-green-100 text-green-700'
  if (state === 'current')
    return isPaused ? 'bg-yellow-100 text-yellow-700' : 'bg-amber-100 text-amber-700'
  return 'bg-neutral-100 text-neutral-400'
}

// ---------------------------------------------------------------------------
// Compact phase item (left column list)
// ---------------------------------------------------------------------------

function CompactPhaseItem({
  phase,
  state,
  isSelected,
  onClick,
  isPaused,
  isCancelled,
}: {
  phase: Phase
  state: NodeState
  isSelected: boolean
  onClick: () => void
  isPaused: boolean
  isCancelled: boolean
}) {
  const Icon = phase.icon

  const iconColor = isCancelled
    ? '#a3a3a3'
    : state === 'completed' || state === 'current'
      ? GOLD
      : '#a3a3a3'

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left w-full transition-all duration-200 border-l-[3px] ${
        isSelected
          ? 'border-l-[#b8a378] bg-[#b8a378]/5'
          : 'border-l-transparent hover:bg-neutral-50'
      }`}
    >
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          isCancelled
            ? 'bg-neutral-100'
            : state === 'completed' || state === 'current'
              ? 'bg-[#b8a378]/10'
              : 'bg-neutral-50'
        }`}
      >
        <Icon className="h-4 w-4" style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <span
          className={`text-sm font-medium block truncate ${
            isCancelled
              ? 'text-neutral-400'
              : state === 'future'
                ? 'text-neutral-400'
                : 'text-neutral-800'
          }`}
        >
          {String(phase.number).padStart(2, '0')}. {phase.name}
        </span>
      </div>
      {isCancelled ? (
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-500 flex-shrink-0">
          Cancelado
        </span>
      ) : (
        <span
          className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${stateBadgeClass(state, isPaused)}`}
        >
          {stateLabel(state, isPaused)}
        </span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Phase detail panel (right column)
// ---------------------------------------------------------------------------

function PhaseDetailPanel({
  phase,
  state,
  isPaused,
  isCancelled,
  selectedProjectId,
  selectedProject,
  onNext,
  onPrev,
  workflowStatus,
  onRefresh,
  isRefreshing,
}: {
  phase: Phase
  state: NodeState
  isPaused: boolean
  isCancelled: boolean
  selectedProjectId: string | null
  selectedProject: any
  onNext?: () => void
  onPrev?: () => void
  workflowStatus?: WorkflowStatus
  onRefresh?: () => void
  isRefreshing?: boolean
}) {
  const navigate = useNavigate()
  const Icon = phase.icon

  const iconColor = isCancelled
    ? '#a3a3a3'
    : state === 'completed' || state === 'current'
      ? GOLD
      : '#a3a3a3'

  return (
    <div className="space-y-5 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
            isCancelled
              ? 'bg-neutral-100'
              : state === 'completed' || state === 'current'
                ? 'bg-[#b8a378]/10'
                : 'bg-neutral-50'
          }`}
        >
          <Icon className="h-6 w-6" style={{ color: iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-neutral-900">
              {String(phase.number).padStart(2, '0')}. {phase.name}
            </h3>
            {isCancelled ? (
              <Badge variant="secondary" className="bg-neutral-200 text-neutral-500 text-[10px]">
                Cancelado
              </Badge>
            ) : (
              <Badge
                variant="secondary"
                className={`text-[10px] ${stateBadgeClass(state, isPaused)}`}
              >
                {stateLabel(state, isPaused)}
              </Badge>
            )}
          </div>
          <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
            {phase.longDescription}
          </p>
        </div>
      </div>

      <Separator />

      {/* Sequential actions — primary section */}
      {phase.actions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-neutral-800">
              Próximos passos
            </h4>
            {onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-neutral-400 hover:text-neutral-600"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {phase.actions.map((action, idx) => {
              const disabled = action.requiresProject && !selectedProjectId
              const done = action.doneCheck?.({ projectId: selectedProjectId, project: selectedProject })
                ?? workflowStatus?.[phase.number]?.fulfilled
                ?? false
              const path = action.projectPath && selectedProjectId
                ? action.projectPath.replace(':id', selectedProjectId)
                : action.path
              const separator = path.includes('?') ? '&' : '?'
              const fullPath = `${path}${separator}phase=${phase.number}`

              return (
                <div
                  key={idx}
                  role={disabled ? undefined : 'button'}
                  tabIndex={disabled ? undefined : 0}
                  onClick={() => {
                    if (!disabled) navigate(fullPath)
                  }}
                  onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault()
                      navigate(fullPath)
                    }
                  }}
                  className={`
                    flex items-center gap-4 px-5 py-4 rounded-xl border-2 transition-all duration-200
                    ${done
                      ? 'border-green-200 bg-green-50/60 hover:border-green-300 hover:shadow-sm cursor-pointer'
                      : disabled
                        ? 'border-neutral-100 bg-neutral-50/50 cursor-default'
                        : action.optional
                          ? 'border-blue-100 bg-blue-50/30 hover:border-blue-300 hover:shadow-md hover:scale-[1.01] cursor-pointer'
                          : 'border-neutral-200 bg-white hover:border-[#b8a378] hover:shadow-md hover:scale-[1.01] cursor-pointer'
                    }
                  `}
                >
                  {/* Step number or check */}
                  {done ? (
                    <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-green-100 text-green-600">
                      <Check className="h-4 w-4" />
                    </div>
                  ) : (
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                        disabled
                          ? 'bg-neutral-100 text-neutral-300'
                          : action.optional
                            ? 'bg-blue-100 text-blue-400'
                            : 'bg-[#b8a378]/15 text-[#b8a378]'
                      }`}
                    >
                      {idx + 1}
                    </div>
                  )}

                  {/* Label */}
                  <div className="flex-1 flex items-center gap-2">
                    <span
                      className={`font-medium ${
                        done ? 'text-green-700 text-sm' : disabled ? 'text-neutral-400 text-sm' : 'text-neutral-800 text-sm'
                      }`}
                    >
                      {action.label}
                    </span>
                    {action.optional && (
                      <span className="text-[10px] font-medium text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded">
                        Opcional
                      </span>
                    )}
                  </div>

                  {/* Status indicator */}
                  {done ? (
                    <div className="flex items-center gap-1.5 text-green-600 flex-shrink-0">
                      <span className="text-xs font-medium">Concluído</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  ) : disabled ? (
                    <span className="text-[11px] text-neutral-400 italic flex-shrink-0">
                      Selecione um projeto
                    </span>
                  ) : (
                    <ArrowRight className={`h-4 w-4 flex-shrink-0 transition-colors ${action.optional ? 'text-blue-300' : 'text-neutral-300 group-hover:text-[#b8a378]'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Tip */}
      <div className="flex gap-3 p-3.5 rounded-lg bg-amber-50/80 border border-amber-100">
        <Lightbulb className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 leading-relaxed">
          {phase.tip}
        </p>
      </div>

      {/* Checklist */}
      <div>
        <h4 className="text-sm font-semibold text-neutral-700 mb-2.5">
          Checklist da fase
        </h4>
        <ul className="space-y-2">
          {phase.checklist.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-600">
              <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-md border border-neutral-200 bg-neutral-50 flex items-center justify-center">
                <span className="text-[10px] text-neutral-400 font-medium">{i + 1}</span>
              </div>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Phase navigation buttons */}
      {(onPrev || onNext) && (
        <div className="flex items-center justify-between pt-2">
          {onPrev ? (
            <Button
              variant="outline"
              size="default"
              className="gap-1.5 border-2 border-neutral-300 text-neutral-600 hover:border-[#b8a378] hover:text-neutral-800 font-medium"
              onClick={onPrev}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
          ) : (
            <div />
          )}
          {onNext ? (
            <Button
              size="default"
              className="gap-1.5 text-white font-semibold border-2 shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`,
                borderColor: GOLD_DARK,
              }}
              onClick={onNext}
            >
              Próximo
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <div />
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Dashboard() {
  const { user, tenant } = useAuthStore()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const canCreate = usePermission('projects:create')

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const openPhaseParam = searchParams.get('openPhase')
  const initialPhaseIdx = openPhaseParam ? parseInt(openPhaseParam) - 1 : 0
  const [selectedPhaseIdx, setSelectedPhaseIdx] = useState(initialPhaseIdx)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchInput, setSearchInput] = useState('')
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ projectId: string; projectName: string } | null>(null)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await queryClient.invalidateQueries({ queryKey: ['projects'] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    if (selectedProjectId) {
      await queryClient.invalidateQueries({ queryKey: ['project-activities', selectedProjectId] })
      await queryClient.invalidateQueries({ queryKey: ['project-progress', selectedProjectId] })
    }
    setTimeout(() => setIsRefreshing(false), 600)
  }

  // Restore selected phase from query param (e.g. /?phase=3)
  useEffect(() => {
    const p = searchParams.get('phase')
    if (p) {
      const idx = parseInt(p, 10) - 1
      if (idx >= 0 && idx < 8) setSelectedPhaseIdx(idx)
    }
  }, [searchParams])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { page, search, status: statusFilter }],
    queryFn: () =>
      projectsAPI.list({
        page,
        limit: 9,
        search: search || undefined,
        status: statusFilter || undefined,
      }),
  })

  const projectsPageData = projectsData?.data || []
  const pagination = projectsData?.pagination || { page: 1, totalPages: 1, total: 0 }

  // Get all projects for selector (no pagination)
  const { data: allProjectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectsAPI.list({ limit: 100 }),
  })

  const projects = allProjectsData?.data || []

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId: string) => projectsAPI.delete(projectId),
    onSuccess: () => {
      toast.success('Projeto deletado com sucesso')
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['projects-all'] })
      setDeleteConfirmation(null)
      if (deleteConfirmation?.projectId === selectedProjectId) {
        setSelectedProjectId(null)
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao deletar projeto')
    },
  })

  // Auto-select when there's exactly one project and none is selected
  useEffect(() => {
    if (!selectedProjectId && projects.length === 1) {
      setSelectedProjectId(projects[0].id)
    }
  }, [projects, selectedProjectId])

  const selectedProject = selectedProjectId
    ? projects.find((p: any) => p.id === selectedProjectId)
    : null

  const currentPhase = getCurrentPhase(selectedProject)

  const isPaused = selectedProject?.status === 'PAUSED'
  const isCancelled = selectedProject?.status === 'CANCELLED'
  const workflowStatus = useWorkflowStatus()

  const completedPhases = selectedProject && !isCancelled
    ? PHASES.filter(p => getNodeState(p.number, currentPhase) === 'completed').length
    : 0

  const statusLabel: Record<string, string> = {
    PLANNING: 'Planejamento',
    IN_PROGRESS: 'Em Andamento',
    PAUSED: 'Pausado',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
  }

  const statusVariant: Record<string, any> = {
    PLANNING: 'planning',
    IN_PROGRESS: 'inProgress',
    PAUSED: 'paused',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
  }

  function formatCurrency(value: number) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  return (
    <div className="space-y-8">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">
          Bem-vindo, {user?.name}!
        </h1>
        <p className="mt-1 text-neutral-600">{tenant?.name}</p>
      </div>

      {/* ── Project Workflow Flow ──────────────────────────────── */}
      <PhaseFlow
        phases={PHASES}
        currentPhase={currentPhase}
        selectedPhaseIdx={selectedPhaseIdx}
        onSelectPhase={setSelectedPhaseIdx}
        isPaused={isPaused}
        isCancelled={isCancelled}
        completedPhases={completedPhases}
        size="large"
        projectName={selectedProject?.name}
      />

      {/* Main content area: Projects (left) + Details (right) */}
      {!selectedProjectId ? (
        /* No project selected — full width projects list */
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-800">
              Meus Projetos
            </h2>
            {canCreate && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Projeto
              </Button>
            )}
          </div>

        {/* Filters */}
        <div className="flex flex-col gap-4 sm:flex-row mb-6">
          <form onSubmit={handleSearch} className="flex flex-1 gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <Input
                placeholder="Buscar projetos..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="secondary">
              Buscar
            </Button>
          </form>

          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value === 'ALL' ? '' : value)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os status</SelectItem>
              {Object.entries(statusLabel).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content */}
        {projectsPageData.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
            <FolderOpen className="h-16 w-16 text-neutral-300" />
            <h3 className="mt-4 text-xl font-medium text-neutral-900">
              {search || statusFilter
                ? 'Nenhum projeto encontrado'
                : 'Nenhum projeto cadastrado'}
            </h3>
            <p className="mt-2 text-neutral-500">
              {search || statusFilter
                ? 'Tente ajustar os filtros de busca.'
                : 'Comece criando seu primeiro projeto.'}
            </p>
            {canCreate && !search && !statusFilter && (
              <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Criar Primeiro Projeto
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Project Cards Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projectsPageData.map((project: any) => {
                const progress =
                  project.evolutions && project.evolutions.length > 0
                    ? project.evolutions[0].percentage
                    : 0
                const isSelected = selectedProjectId === project.id

                return (
                  <Card
                    key={project.id}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'ring-2 ring-primary' : ''
                    }`}
                    onClick={() => setSelectedProjectId(project.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-primary" />
                          <CardTitle className="text-base line-clamp-1">
                            {project.name}
                          </CardTitle>
                        </div>
                        <Badge variant={statusVariant[project.status]}>
                          {statusLabel[project.status]}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {project.description && (
                        <p className="text-sm text-neutral-600 line-clamp-2">
                          {project.description}
                        </p>
                      )}

                      <div className="flex items-center gap-1 text-sm text-neutral-500">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="line-clamp-1">
                          {project.address?.city}/{project.address?.state}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-500">
                          {formatCurrency(project.budget)}
                        </span>
                        <span className="text-neutral-500">
                          {project._count?.units || 0} unid.
                        </span>
                      </div>

                      {/* Progress */}
                      <div className="flex items-center gap-3">
                        <Progress value={progress} className="flex-1 h-2" />
                        <span className="text-sm font-medium text-neutral-700">
                          {progress}%
                        </span>
                      </div>

                      {project.startDate && (
                        <div className="flex items-center gap-1 text-xs text-neutral-400">
                          <Calendar className="h-3 w-3" />
                          <span>
                            Início:{' '}
                            {new Date(project.startDate).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-6">
                <p className="text-sm text-neutral-500">
                  Mostrando {projectsPageData.length} de {pagination.total} projetos
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm text-neutral-600">
                    {page} / {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Próximo
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      ) : (
      <div className="space-y-3">
        {/* Header com Voltar */}
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-neutral-500 hover:text-neutral-700 -ml-2"
          onClick={() => setSelectedProjectId(null)}
        >
          ← Voltar
        </Button>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:h-[calc(100vh-280px)]">
          {/* Left: Selected Project + Phase Navigator */}
          <div className="flex flex-col h-full min-h-0">
            {/* Selected Project Card */}
            <div>
              <Card className="border-neutral-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold text-sm text-neutral-900 line-clamp-2 flex-1">
                    {selectedProject?.name}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-neutral-400 hover:text-neutral-600"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem disabled>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          if (selectedProject) {
                            setDeleteConfirmation({
                              projectId: selectedProject.id,
                              projectName: selectedProject.name,
                            })
                          }
                        }}
                        className="text-red-600 focus:bg-red-50 focus:text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Deletar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="secondary" className="text-[10px]">
                    {statusLabel[selectedProject?.status]}
                  </Badge>
                  <span className="text-xs text-neutral-500">
                    {selectedProject?._count?.units || 0} un.
                  </span>
                </div>
                {selectedProject?.address?.city && (
                  <div className="text-[11px] text-neutral-400">
                    {selectedProject.address.city}/{selectedProject.address.state}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Phase Navigator - Full Height */}
          <div className="flex-1 flex flex-col mt-4 min-h-0">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2 px-1">
              Fases
            </h3>
            <Card className="p-2 border-neutral-200 flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="space-y-0.5 overflow-y-auto flex-1 pr-1">
                {PHASES.map((phase, idx) => {
                  const phaseState = isCancelled
                    ? ('future' as NodeState)
                    : getNodeState(phase.number, currentPhase)
                  return (
                    <CompactPhaseItem
                      key={phase.number}
                      phase={phase}
                      state={phaseState}
                      isSelected={selectedPhaseIdx === idx}
                      onClick={() => setSelectedPhaseIdx(idx)}
                      isPaused={isPaused && phase.number === currentPhase}
                      isCancelled={isCancelled}
                    />
                  )
                })}
              </div>
            </Card>
          </div>
        </div>

        {/* Right: Phase Details (Full Width) */}
        <div className="h-full min-h-0">
          <Card className="p-6 border-neutral-200 h-full overflow-auto">
            <PhaseDetailPanel
              key={selectedPhaseIdx}
              phase={PHASES[selectedPhaseIdx]}
              state={
                isCancelled
                  ? ('future' as NodeState)
                  : getNodeState(PHASES[selectedPhaseIdx].number, currentPhase)
              }
              isPaused={isPaused && PHASES[selectedPhaseIdx].number === currentPhase}
              isCancelled={isCancelled}
              selectedProjectId={selectedProjectId}
              selectedProject={selectedProject}
              onPrev={selectedPhaseIdx > 0 ? () => setSelectedPhaseIdx(selectedPhaseIdx - 1) : undefined}
              onNext={selectedPhaseIdx < PHASES.length - 1 ? () => setSelectedPhaseIdx(selectedPhaseIdx + 1) : undefined}
              workflowStatus={workflowStatus}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            />
          </Card>
        </div>
      </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmation} onOpenChange={(open) => !open && setDeleteConfirmation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-neutral-600">
              Tem certeza que deseja deletar o projeto <strong>{deleteConfirmation?.projectName}</strong>? Esta ação não pode ser desfeita.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteConfirmation(null)}
              disabled={deleteProjectMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                if (deleteConfirmation?.projectId) {
                  deleteProjectMutation.mutate(deleteConfirmation.projectId)
                }
              }}
              disabled={deleteProjectMutation.isPending}
            >
              {deleteProjectMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Deletar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <ProjectFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  )
}
